import { Loader, Scale, ShieldAlert, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, hlaskaZChyby } from '../api/client.js';
import type { HlidaceZakazky, Rekapitulace, Zakazka } from '../api/types.js';
import { formatKc, formatProcento } from '../lib/format.js';
import { useAuth } from '../auth/AuthContext.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import { ZMENA_STAV_BARVA, ZMENA_STAV_LABEL } from '../lib/labels.js';
import {
  Badge,
  Btn,
  Chyba,
  Dialog,
  Karta,
  Menu,
  Nacitani,
  Napoveda,
  StatKarta,
  td,
  th,
} from '../components/ui.js';
import { LegendaZmen, ProvedenoZnak } from './zmeny/ZmenyPage.js';
import { ProcesViceprace } from '../components/ProcesViceprace.js';

/**
 * VÍCEPRÁCE — přehled a marže zakázky (wireframe 13 §4, blueprint 16 §4):
 * odpověď na „kolik na změnách vyděláváme a co nám hoří" do 10 vteřin. `?` =
 * neoceněná strana (nikdy 0), A! = provedeno na riziko červeně, sporné fialově
 * a v součtu výrazně. JEDEN „Export ▾" místo čtyř tlačítek (interní / pro
 * investora — investorský = potvrzení, nevratné odeslání citlivých čísel ven).
 * Zeď na serveru: investorská varianta jde bez nákladů a marží.
 */
/** České skloňování počtu změn. */
function sklonujZmeny(n: number): string {
  if (n === 1) return 'změna';
  if (n >= 2 && n <= 4) return 'změny';
  return 'změn';
}

export function RekapitulacePage() {
  const { id } = useParams();
  const { maFinance } = useAuth();

  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  // S :id v cestě (proklik z detailu) použij ho; jinak globální přepínač (B1.2).
  const { vybranaId } = useVybranaZakazka(zakazky);
  const zakazkaId = id ?? vybranaId;

  const { data, isLoading, error } = useQuery({
    queryKey: ['rekapitulace', zakazkaId],
    queryFn: () => api.get<Rekapitulace>(`/api/zakazky/${zakazkaId}/rekapitulace`),
    enabled: !!zakazkaId,
  });
  const { data: hlidace } = useQuery({
    queryKey: ['hlidace', zakazkaId],
    queryFn: () => api.get<HlidaceZakazky>(`/api/zakazky/${zakazkaId}/hlidace`),
    enabled: !!zakazkaId,
  });

  if (!zakazkaId && !zakazky) return <Nacitani text="Načítám zakázky…" />;

  return (
    <div className="ph-mask contents">
      <ProcesViceprace krok={3} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">
            Rekapitulace{data ? ` — ${data.zakazka.nazev}` : ''}
          </h1>
          <p className="text-base text-text-secondary">Kolik na změnách vyděláváme a co hoří.</p>
        </div>
        {zakazkaId && <ExportMenu zakazkaId={zakazkaId} />}
      </div>

      {/* Panel hlídačů zakázky (duplikát dashboardu) — sbalený do „⚠ N hlídačů
          [rozbalit]", ať tabulka není odsunutá pod ohyb (blueprint 16 §4). */}
      {hlidace && (hlidace.alarmy.length > 0 || hlidace.pripominky.length > 0) && (
        <details
          className={`rounded-lg border border-border border-l-4 bg-surface p-4 ${
            hlidace.alarmy.length > 0 ? 'border-l-error' : 'border-l-warning'
          }`}
          open={hlidace.alarmy.length > 0}
        >
          <summary className="flex cursor-pointer list-none items-center gap-2 font-semibold text-text-primary">
            <span aria-hidden="true">{hlidace.alarmy.length > 0 ? '⚠️' : '💡'}</span>
            {hlidace.alarmy.length + hlidace.pripominky.length}{' '}
            {hlidace.alarmy.length + hlidace.pripominky.length === 1 ? 'hlídač' : 'hlídačů'}{' '}
            <span className="text-sm font-normal text-primary underline underline-offset-2">
              rozbalit
            </span>
          </summary>
          <div className="mt-3 flex flex-col gap-2">
            {hlidace.alarmy.map((h, i) => (
              <p key={`a${i}`} className="font-medium text-error-text">
                {h.text}{' '}
                {h.zmenaId && (
                  <Link to={`/zmeny/${h.zmenaId}`} className="underline underline-offset-2">
                    otevřít Změnu
                  </Link>
                )}
              </p>
            ))}
            {hlidace.pripominky.map((h, i) => (
              <p key={`p${i}`} className="text-text-primary">
                {h.text}{' '}
                {h.zmenaId && (
                  <Link to={`/zmeny/${h.zmenaId}`} className="underline underline-offset-2">
                    otevřít Změnu
                  </Link>
                )}
                {h.nalezId && (
                  <Link to="/nalezy" className="underline underline-offset-2">
                    otevřít Nálezy
                  </Link>
                )}
              </p>
            ))}
          </div>
        </details>
      )}

      {isLoading && <Nacitani text="Načítám rekapitulaci…" />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}

      {data?.zzvz?.verejnaZakazka && (
        <section className="rounded-xl border border-info-border bg-info-bg p-4 text-info-text">
          <p className="flex items-center gap-2 font-bold">
            Veřejná zakázka — hlídáme limity změn
            {data.sod?.cena !== undefined && ` (smlouva: ${formatKc(data.sod.cena)})`}
            {/* Paragrafové znění + procentní limity on-demand za „?" (blueprint 16). */}
            <Napoveda titulek="Limity změn u veřejné zakázky">
              <p>
                U veřejné zakázky platí zákonné limity změn (§ 222 ZZVZ — 15/30 % ceny smlouvy).
                Hlídání zatím počítáme ručně — aplikace ho doplní v další verzi.
                {data.zzvz.cerpanoProcent != null &&
                  ` Čerpání změn zatím ${formatProcento(data.zzvz.cerpanoProcent)} ceny smlouvy` +
                    ' (absolutní hodnoty — méněpráce čerpání nesnižují).'}
              </p>
            </Napoveda>
          </p>
          {data.zzvz.cerpanoProcent != null && (
            <p className="text-sm">
              Čerpání změn zatím {formatProcento(data.zzvz.cerpanoProcent)} ceny smlouvy.
            </p>
          )}
        </section>
      )}

      {data && data.radky.length === 0 && (
        <p className="rounded-xl border border-border bg-white p-6 text-text-secondary">
          Zatím žádné změny. Nálezy z terénu se objeví v sekci Nálezy.
        </p>
      )}

      {/* Odpověď „kolik vyděláváme a co hoří" NAHOŘE jako čísla dne — ne až
          ve footeru tabulky (účel obrazovky: odpověď do 10 vteřin). */}
      {maFinance && data && data.radky.length > 0 && (
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <StatKarta
            popisek="Marže schválených"
            hodnota={formatKc(data.kumulativy.schvalene.marze)}
            detail={`${data.kumulativy.schvalene.pocet} ${sklonujZmeny(data.kumulativy.schvalene.pocet)} · výnos ${formatKc(data.kumulativy.schvalene.vynos)}`}
            ton={
              data.kumulativy.schvalene.pocet === 0
                ? 'neutral'
                : data.kumulativy.schvalene.marze?.startsWith('-')
                  ? 'error'
                  : 'success'
            }
            ikona={<TrendingUp size={18} strokeWidth={1.75} />}
          />
          <StatKarta
            popisek="V procesu"
            hodnota={data.kumulativy.vProcesu.pocet}
            detail={`výnos min. ${formatKc(data.kumulativy.vProcesu.vynosMin)}+`}
            ikona={<Loader size={18} strokeWidth={1.75} />}
          />
          <StatKarta
            popisek="Sporné (claim)"
            hodnota={data.kumulativy.sporne.pocet}
            detail={
              data.kumulativy.sporne.pocet > 0
                ? `objem ${formatKc(data.kumulativy.sporne.objemVynos)}`
                : 'žádný spor'
            }
            ton={data.kumulativy.sporne.pocet > 0 ? 'warning' : 'neutral'}
            ikona={<Scale size={18} strokeWidth={1.75} />}
          />
          <StatKarta
            popisek="Na riziko (A!)"
            hodnota={data.kumulativy.provedenoBezPodpisu.pocet}
            detail={
              data.kumulativy.provedenoBezPodpisu.pocet > 0
                ? `náklad ${formatKc(data.kumulativy.provedenoBezPodpisu.naklad)}`
                : 'nic bez podpisu'
            }
            ton={data.kumulativy.provedenoBezPodpisu.pocet > 0 ? 'error' : 'neutral'}
            ikona={<ShieldAlert size={18} strokeWidth={1.75} />}
          />
        </div>
      )}

      {/* Nárok na prodloužení lhůty — při sporu stejně cenný jako peníze. */}
      {data && (data.kumulativy.dopadNaTerminDny ?? 0) > 0 && (
        <p className="text-sm text-text-secondary">
          Schválené změny prodlužují termín Díla celkem o{' '}
          <span className="font-bold text-text-primary">
            {data.kumulativy.dopadNaTerminDny} dní
          </span>
          .
        </p>
      )}

      {data && data.radky.length > 0 && (
        <Karta>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Č.</th>
                  <th className={th}>Změna</th>
                  <th className={th}>Objekt</th>
                  <th className={th}>Stav</th>
                  <th
                    className={`${th} text-center`}
                    title="provedeno: A ano / N ne / A! na riziko"
                  >
                    Provedeno
                  </th>
                  <th className={`${th} text-right`}>Výnos</th>
                  <th className={`${th} text-right`}>Náklad</th>
                  <th className={`${th} text-right`}>Marže</th>
                </tr>
              </thead>
              <tbody>
                {data.radky.map((r) => {
                  const ztratova = r.marze != null && r.marze.startsWith('-');
                  return (
                    <tr key={r.zmenaId} className={r.stav === 'sporna' ? 'bg-sporna-bg' : ''}>
                      <td className={`${td} font-semibold whitespace-nowrap`}>
                        <Link
                          to={`/zmeny/${r.zmenaId}`}
                          className="text-primary underline underline-offset-2"
                        >
                          {r.cislo ?? r.zmenaId}
                        </Link>
                      </td>
                      <td className={td}>
                        {r.popis}
                        {r.nesemeSami && (
                          <span className="ml-2 text-sm text-warning-text">(neseme sami)</span>
                        )}
                      </td>
                      <td className={td}>{r.objekt ?? '—'}</td>
                      <td className={td}>
                        <Badge barva={ZMENA_STAV_BARVA[r.stav]}>{ZMENA_STAV_LABEL[r.stav]}</Badge>
                      </td>
                      <td className={`${td} text-center`}>
                        <ProvedenoZnak provedeno={r.provedeno} />
                      </td>
                      <td className={`${td} text-right whitespace-nowrap`}>
                        {formatKc(r.vynos)}
                        {r.spornyVynos ? '?' : ''}
                      </td>
                      <td className={`${td} text-right whitespace-nowrap`}>{formatKc(r.naklad)}</td>
                      <td
                        className={`${td} text-right font-semibold whitespace-nowrap ${
                          r.marze == null
                            ? 'text-info-text'
                            : ztratova
                              ? 'text-error-text'
                              : 'text-success-text'
                        }`}
                      >
                        {formatKc(r.marze)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-bold">
                  <td className={td} colSpan={5}>
                    Σ schválené + fakturovatelné ({data.kumulativy.schvalene.pocet}) — netto, bez
                    DPH
                  </td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    {formatKc(data.kumulativy.schvalene.vynos)}
                  </td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    {formatKc(data.kumulativy.schvalene.naklad)}
                  </td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    {formatKc(data.kumulativy.schvalene.marze)}
                  </td>
                </tr>
                <tr>
                  <td className={td} colSpan={5}>
                    Σ v procesu ({data.kumulativy.vProcesu.pocet}) — minimálně tolik, může růst
                  </td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    {formatKc(data.kumulativy.vProcesu.vynosMin)}+
                  </td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    {formatKc(data.kumulativy.vProcesu.nakladMin)}+
                  </td>
                  <td className={`${td} text-right text-info-text`}>?</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {data.kumulativy.sporne.pocet > 0 && (
            <p className="mt-3 rounded-lg border border-sporna-border bg-sporna-bg p-3 font-bold text-sporna-text">
              ⚠ Objem sporných změn: {formatKc(data.kumulativy.sporne.objemVynos)} (
              {data.kumulativy.sporne.pocet}{' '}
              {data.kumulativy.sporne.pocet === 1 ? 'změna' : 'změny'}) — největší riziko, řešit.
            </p>
          )}
          {data.kumulativy.provedenoBezPodpisu.pocet > 0 && (
            <p className="mt-2 rounded-lg border border-error-border bg-error-bg p-3 font-bold text-error-text">
              ⚠ Provedeno bez schválení (na riziko): {data.kumulativy.provedenoBezPodpisu.pocet}{' '}
              {data.kumulativy.provedenoBezPodpisu.pocet === 1 ? 'změna' : 'změny'} za{' '}
              {formatKc(data.kumulativy.provedenoBezPodpisu.naklad)} nákladu.
            </p>
          )}
          {/* Ceny bez DPH jednou drobně; vysvětlivky zkratek za „?" (blueprint 16). */}
          <div className="mt-3 flex items-center gap-2 text-sm text-text-secondary">
            <span>Částky netto, ceny bez DPH.</span>
            <LegendaZmen />
          </div>
        </Karta>
      )}
    </div>
  );
}

/**
 * JEDEN „Export ▾" místo čtyř tlačítek v řadě (blueprint 16 §4): rozbalí volbu
 * interní / pro investora × XLSX / PDF. Investorský export = potvrzení (nevratné
 * odeslání citlivých čísel ven, byť zeď na serveru pošle jen výnosovou stranu).
 * Nic z funkce nezmizelo — všechny čtyři exporty jsou dostupné, jen ne v řadě.
 */
function ExportMenu({ zakazkaId }: { zakazkaId: string }) {
  const [investorFormat, setInvestorFormat] = useState<'xlsx' | 'pdf' | null>(null);
  const odkaz = (varianta: 'interni' | 'investor', format: 'xlsx' | 'pdf') =>
    `/api/zakazky/${zakazkaId}/rekapitulace/export.${format}?varianta=${varianta}`;

  return (
    <>
      <Menu
        stitek="Export ▾"
        align="right"
        spoust="inline-flex min-h-11 cursor-pointer items-center gap-1 rounded-md bg-primary px-4 text-base font-medium text-white transition-colors duration-100 hover:bg-primary-hover"
      >
        <p className="px-2 pt-1 text-sm font-semibold text-text-secondary">Interní (obě strany)</p>
        <a
          href={odkaz('interni', 'xlsx')}
          className="inline-flex min-h-11 items-center rounded-md px-3 text-base font-medium text-text-primary hover:bg-bg"
        >
          Excel (XLSX)
        </a>
        <a
          href={odkaz('interni', 'pdf')}
          className="inline-flex min-h-11 items-center rounded-md px-3 text-base font-medium text-text-primary hover:bg-bg"
        >
          PDF
        </a>
        <p className="mt-1 border-t border-border px-2 pt-2 text-sm font-semibold text-text-secondary">
          Pro investora (bez nákladů)
        </p>
        <button
          type="button"
          onClick={() => setInvestorFormat('xlsx')}
          className="inline-flex min-h-11 items-center rounded-md px-3 text-left text-base font-medium text-text-primary hover:bg-bg"
        >
          Excel (XLSX)…
        </button>
        <button
          type="button"
          onClick={() => setInvestorFormat('pdf')}
          className="inline-flex min-h-11 items-center rounded-md px-3 text-left text-base font-medium text-text-primary hover:bg-bg"
        >
          PDF…
        </button>
        <p className="mt-1 max-w-56 border-t border-border px-2 pt-2 pb-1 text-xs text-text-secondary">
          Vzorový formát — pošlete nám svou šablonu rekapitulace a přizpůsobíme ji.
        </p>
      </Menu>

      {investorFormat && (
        <Dialog nadpis="Export pro investora" onZavrit={() => setInvestorFormat(null)}>
          <div className="flex flex-col gap-4">
            <p>
              Investorský export posílá čísla ven z firmy. Obsahuje jen výnosovou stranu — bez
              nákladů a marží (zeď na serveru). Vytvořit soubor?
            </p>
            <div className="flex gap-3">
              <a
                href={odkaz('investor', investorFormat)}
                onClick={() => setInvestorFormat(null)}
                className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 text-base font-medium text-white hover:bg-primary-hover"
              >
                Exportovat ({investorFormat.toUpperCase()})
              </a>
              <Btn size="desktop" varianta="secondary" onClick={() => setInvestorFormat(null)}>
                Zrušit
              </Btn>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
